// src/components/CrashCourse/FinalExam/FinalExam.jsx
//
// Administers the Final Certification Exam. Accepts a `questions` array
// shaped like the Django `quizzes.ExamQuestion` model (see finalExamData.js)
// so it works identically against local mock data or a live API response.
//
// On submit, calls onComplete(attempt) where attempt is:
//   { score, total, pct, pass, missedByCategory, answers, date }
// `answers` is an array of { questionId, chosenLetter, correctLetter, correct }
// — pass this straight to your backend as the payload for creating a
// `quizzes.UserExamAttempt` row.

import React, { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";

const LETTERS = ["A", "B", "C", "D"];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function FinalExam({ questions, passThreshold = 80, onComplete, isDark }) {
  const D       = isDark;
  const panel   = D ? "#142a4d" : "#ffffff";
  const border  = D ? "rgba(255,255,255,0.10)" : "#e2e8f0";
  const text    = D ? "#f0f4f8" : "#0d1f3c";
  const textSub = D ? "rgba(240,244,248,0.55)" : "#64748b";
  const mid     = D ? "#1a3358" : "#f0f4f8";
  const gold    = "#c9971c";
  const goldL   = "#fbbf24";

  const [queue]   = useState(() => shuffle(questions));
  const [idx, setIdx]         = useState(0);
  const [answers, setAnswers] = useState(() => new Array(questions.length).fill(null));

  const q = queue[idx];
  const total = queue.length;
  const answeredCount = answers.filter((a) => a !== null).length;
  const chosen = answers[idx];

  const options = useMemo(() => ([
    { letter: "A", text: q.option_a },
    { letter: "B", text: q.option_b },
    { letter: "C", text: q.option_c },
    { letter: "D", text: q.option_d },
  ]), [q]);

  const selectAnswer = (letter) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[idx] = letter;
      return next;
    });
  };

  const goNext = () => idx < total - 1 && setIdx(idx + 1);
  const goPrev = () => idx > 0 && setIdx(idx - 1);

  const submit = () => {
    let score = 0;
    const missedByCategory = {};
    const detailedAnswers = queue.map((question, i) => {
      const correct = answers[i] === question.correct_answer;
      if (correct) score++;
      else missedByCategory[question.category || "General"] = (missedByCategory[question.category || "General"] || 0) + 1;
      return {
        questionId: question.id,
        chosenLetter: answers[i],
        correctLetter: question.correct_answer,
        correct,
      };
    });
    const pct = Math.round((score / total) * 100);
    onComplete({
      score,
      total,
      pct,
      pass: pct >= passThreshold,
      missedByCategory,
      answers: detailedAnswers,
      date: new Date().toISOString(),
    });
  };

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "clamp(18px,3vw,28px) clamp(14px,4vw,28px)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{
          fontSize: 11, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase",
          background: "rgba(201,151,28,0.15)", border: "1px solid rgba(201,151,28,0.3)",
          color: gold, borderRadius: 99, padding: "5px 13px",
        }}>
          {q.category || "General"}
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, color: textSub }}>Question {idx + 1} of {total}</span>
      </div>

      <div style={{ height: 8, borderRadius: 99, background: D ? "rgba(255,255,255,0.08)" : "#e2e8f0", overflow: "hidden", marginBottom: 22 }}>
        <div style={{
          height: "100%", borderRadius: 99, background: `linear-gradient(90deg,${gold},${goldL})`,
          width: `${((idx + 1) / total) * 100}%`, transition: "width .3s ease",
        }} />
      </div>

      <div style={{ background: panel, border: `1px solid ${border}`, borderRadius: 18, padding: "clamp(20px,3vw,30px)", marginBottom: 18 }}>
        <p style={{ fontSize: "clamp(15px,2vw,18px)", fontWeight: 800, lineHeight: 1.6, color: text, margin: "0 0 20px" }}>
          {q.question_text}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {options.map((opt) => {
            const isSelected = chosen === opt.letter;
            return (
              <button
                key={opt.letter}
                onClick={() => selectAnswer(opt.letter)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 12, padding: "15px 17px", borderRadius: 13, textAlign: "left", fontFamily: "inherit",
                  fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "all .15s",
                  border: `1.5px solid ${isSelected ? "rgba(201,151,28,0.5)" : border}`,
                  background: isSelected ? "rgba(201,151,28,0.12)" : (D ? "rgba(255,255,255,0.04)" : "#fafafa"),
                  color: isSelected ? goldL : (D ? "rgba(240,244,248,0.80)" : "#334155"),
                }}
              >
                <span><strong style={{ marginRight: 8 }}>{opt.letter}.</strong>{opt.text}</span>
                {isSelected && <CheckCircle2 size={18} style={{ color: goldL, flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <button
          onClick={goPrev}
          disabled={idx === 0}
          style={{
            display: "flex", alignItems: "center", gap: 6, background: D ? "rgba(255,255,255,0.06)" : "#f1f5f9",
            color: text, border: `1px solid ${border}`, borderRadius: 12, fontWeight: 800, fontSize: 14,
            padding: "13px 22px", cursor: idx === 0 ? "not-allowed" : "pointer", opacity: idx === 0 ? 0.4 : 1,
            fontFamily: "inherit",
          }}
        >
          <ChevronLeft size={16} /> Previous
        </button>

        <span style={{ fontSize: 12.5, color: textSub }}>{answeredCount} of {total} answered</span>

        {idx === total - 1 ? (
          <button
            onClick={submit}
            disabled={answeredCount < total}
            style={{
              display: "flex", alignItems: "center", gap: 8, border: "none", borderRadius: 12, fontWeight: 800,
              fontSize: 14, padding: "13px 24px", cursor: answeredCount < total ? "not-allowed" : "pointer",
              opacity: answeredCount < total ? 0.4 : 1, fontFamily: "inherit",
              background: `linear-gradient(135deg,${gold},${goldL})`, color: "#0d1f3c",
              boxShadow: `0 4px 14px rgba(201,151,28,0.40)`,
            }}
          >
            Submit Final Exam <ChevronRight size={16} />
          </button>
        ) : (
          <button
            onClick={goNext}
            style={{
              display: "flex", alignItems: "center", gap: 8, border: "none", borderRadius: 12, fontWeight: 800,
              fontSize: 14, padding: "13px 24px", cursor: "pointer", fontFamily: "inherit",
              background: `linear-gradient(135deg,${gold},${goldL})`, color: "#0d1f3c",
              boxShadow: `0 4px 14px rgba(201,151,28,0.40)`,
            }}
          >
            Next <ChevronRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
